// ═══════════════════════════════════════
// ATLAS BEASTS — BATTLE/ENCOUNTER
// Pokemon Go-style mobile overlay
// ═══════════════════════════════════════

import { useState } from 'react'

const MOVES = [
  { name: 'Flame Strike', type: 'fire', power: 85, accuracy: 100 },
  { name: 'Water Blast', type: 'water', power: 75, accuracy: 95 },
  { name: 'Rock Throw', type: 'earth', power: 70, accuracy: 100 },
  { name: 'Wind Slash', type: 'air', power: 80, accuracy: 90 },
  { name: 'Void Pulse', type: 'void', power: 90, accuracy: 85 },
]

const ELEMENT_COLORS = {
  fire: '#FF4800',
  water: '#00F0FF',
  earth: '#39FF14',
  air: '#B8EEFF',
  void: '#BF00FF',
  aether: '#FFE800',
}

export default function Battle() {
  const [turn, setTurn] = useState(1)
  const [playerHP, setPlayerHP] = useState(100)
  const [enemyHP, setEnemyHP] = useState(100)
  const [battleLog, setBattleLog] = useState<string[]>(['Battle started!'])
  const [isAttacking, setIsAttacking] = useState(false)

  const handleMove = (move: typeof MOVES[0]) => {
    setIsAttacking(true)
    
    // Simulate battle
    setTimeout(() => {
      const damage = Math.floor(move.power * (Math.random() * 0.4 + 0.8))
      setEnemyHP(prev => Math.max(0, prev - damage))
      setBattleLog(prev => [`Your ${move.name} dealt ${damage} damage!`, ...prev])
      setTurn(prev => prev + 1)
      setIsAttacking(false)

      // Enemy counter-attack
      setTimeout(() => {
        const enemyDamage = Math.floor(40 * (Math.random() * 0.4 + 0.8))
        setPlayerHP(prev => Math.max(0, prev - enemyDamage))
        setBattleLog(prev => [`Enemy attacks for ${enemyDamage} damage!`, ...prev])
      }, 500)
    }, 1000)
  }

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Battle Info */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <div className="flex items-center gap-2">
          <span className="text-2xl">⚔️</span>
          <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Battle</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>
            Turn {turn}
          </span>
        </div>
      </div>

      {/* Battle Arena */}
      <div className="flex-1 flex flex-col p-4">
        
        {/* Enemy */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-4xl">🐉</span>
              <div>
                <p className="font-bold text-sm text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  Void Drake
                </p>
                <span className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
                  Lv. 15
                </span>
              </div>
            </div>
            <span className="font-bold text-lg text-[#FF4800]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              {enemyHP}%
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(58,58,90,0.3)' }}>
            <div 
              className="h-full transition-all duration-500"
              style={{ 
                width: `${enemyHP}%`,
                background: 'linear-gradient(90deg, #FF480060, #FF4800)',
              }}
            />
          </div>
        </div>

        {/* VS Divider */}
        <div className="text-center mb-8">
          <span className="text-4xl font-bold" style={{ color: '#FFE800', fontFamily: 'Rajdhani, system-ui' }}>VS</span>
        </div>

        {/* Player */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-4xl">🦊</span>
              <div>
                <p className="font-bold text-sm text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  Foxfire
                </p>
                <span className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
                  Lv. 12
                </span>
              </div>
            </div>
            <span className="font-bold text-lg text-[#39FF14]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              {playerHP}%
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(58,58,90,0.3)' }}>
            <div 
              className="h-full transition-all duration-500"
              style={{ 
                width: `${playerHP}%`,
                background: 'linear-gradient(90deg, #39FF1460, #39FF14)',
              }}
            />
          </div>
        </div>

        {/* Battle Log */}
        <div className="flex-1 rounded-xl border p-3 overflow-hidden" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <p className="text-[10px] text-[#5A5A7A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Space Mono, monospace' }}>
            Battle Log
          </p>
          <div className="space-y-1 overflow-y-auto max-h-24">
            {battleLog.slice(0, 5).map((log, index) => (
              <p key={index} className="text-xs text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
                {log}
              </p>
            ))}
          </div>
        </div>

      </div>

      {/* Move Selection */}
      <div className="p-4 border-t" style={{ borderColor: 'rgba(58,58,90,0.5)', background: 'rgba(13,13,21,0.95)' }}>
        <p className="text-[10px] text-[#5A5A7A] uppercase tracking-wider mb-3" style={{ fontFamily: 'Space Mono, monospace' }}>
          Choose Your Move
        </p>
        <div className="grid grid-cols-3 gap-2">
          {MOVES.map(move => (
            <button
              key={move.name}
              onClick={() => !isAttacking && handleMove(move)}
              disabled={isAttacking}
              className={`rounded-xl border p-3 transition-all active:scale-95 hover:scale-105 text-center ${
                isAttacking ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ 
                background: 'rgba(13,13,21,0.95)', 
                borderColor: `${ELEMENT_COLORS[move.type as keyof typeof ELEMENT_COLORS]}40`,
                boxShadow: `0 0 12px ${ELEMENT_COLORS[move.type as keyof typeof ELEMENT_COLORS]}15`,
              }}
            >
              <p className="font-bold text-xs text-[#EAF6FF] uppercase mb-1" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {move.name}
              </p>
              <div className="space-y-0.5">
                <p className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
                  PWR: {move.power}
                </p>
                <p className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
                  ACC: {move.accuracy}%
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Victory/Defeat Overlay */}
      {(playerHP <= 0 || enemyHP <= 0) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(2,5,16,0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <span className="text-6xl mb-4 block">{enemyHP <= 0 ? '🏆' : '💀'}</span>
            <h2 className="font-bold text-2xl uppercase mb-2" style={{ 
              color: enemyHP <= 0 ? '#39FF14' : '#FF4800',
              fontFamily: 'Rajdhani, system-ui',
            }}>
              {enemyHP <= 0 ? 'Victory!' : 'Defeated'}
            </h2>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {enemyHP <= 0 ? 'You defeated the Void Drake!' : 'Your creature was defeated.'}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
                color: '#020510',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              Return to Map
            </button>
          </div>
        </div>
      )}

    </div>
  )
}
